import { ExpandMore as ExpandIcon, OpenInNew as OpenIcon } from "@mui/icons-material";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useArgusInstances } from "../../hooks/argus";
import { appendArgusParams } from "../../lib/argus/argus-params";
import { useArgusConnectionContext } from "../../providers/argus-connection-provider";
import { BRAND_PRIMARY, RADIUS_MD } from "../../theme/theme";

export interface ArgusNavigateCardProps {
  path: string;
  label: string;
  reason?: string | null;
  /**
   * When `alreadyNavigated` is true the card renders as a post-hoc
   * breadcrumb instead of a clickable CTA — used when
   * `argusAutoNavigate` is enabled and the page already changed.
   */
  alreadyNavigated?: boolean;
}

/**
 * Compact inline card rendered by `ArgusMessageList` when the agent calls
 * the `navigate` tool. Reasoning is collapsed behind a chevron toggle.
 */
export const ArgusNavigateCard = ({
  path,
  label,
  reason,
  alreadyNavigated = false,
}: ArgusNavigateCardProps) => {
  const navigate = useNavigate();
  const { activeSessionId, setSidebarOpen } = useArgusConnectionContext();
  const { instances } = useArgusInstances({ limit: 1 });
  const instanceId = instances?.[0]?.id ?? null;
  const [reasonOpen, setReasonOpen] = useState(false);

  const handleOpen = () => {
    navigate(
      appendArgusParams(path, {
        sessionId: activeSessionId,
        instanceId,
      }),
    );
    setSidebarOpen(true);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${BRAND_PRIMARY}33`,
        borderRadius: `${RADIUS_MD}px`,
        backgroundColor: "rgba(22, 0, 234, 0.03)",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          paddingX: 1.5,
          paddingY: 0.75,
          cursor: alreadyNavigated ? "default" : "pointer",
          "&:hover": alreadyNavigated ? {} : { backgroundColor: "rgba(22, 0, 234, 0.06)" },
        }}
        onClick={alreadyNavigated ? undefined : handleOpen}
      >
        <OpenIcon sx={{ fontSize: 16, color: "primary.main", flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontFamily: "monospace",
            flexShrink: 0,
          }}
        >
          {path}
        </Typography>
        {alreadyNavigated && (
          <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
            Navigated
          </Typography>
        )}
        {reason && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setReasonOpen((prev) => !prev);
            }}
            sx={{
              padding: 0.25,
              transform: reasonOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            <ExpandIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Stack>
      {reason && (
        <Collapse in={reasonOpen}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              paddingX: 1.5,
              paddingBottom: 0.75,
              borderTop: "1px solid",
              borderColor: "divider",
              paddingTop: 0.5,
            }}
          >
            {reason}
          </Typography>
        </Collapse>
      )}
    </Box>
  );
};
